import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  FileText,
  Grid2X2,
  LayoutDashboard,
  LogOut,
  Menu,
  MapPinned,
  QrCode,
  Route,
  Search,
  Settings,
  Users,
  X
} from 'lucide-react';
import { NavLink, useLocation, useNavigate } from '../../app/router';
import type { ReactNode } from 'react';
import { clearToken, readToken, tenantTokenKey } from '../../app/session';
import { Logo } from '../../components/Logo';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { useTranslation, type AppLocale } from '../../i18n/i18n';
import { ApiError, apiRequest } from '../../lib/api';
import {
  TenantAppContext,
  type TenantContextData
} from './TenantAppContext';

export function TenantShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = readToken(tenantTokenKey);
  const { locale, setLocale, t } = useTranslation();
  const [context, setContext] = useState<TenantContextData | null>(null);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileMenu, setMobileMenu] = useState(false);
  const [globalQuery, setGlobalQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    type: 'TASK' | 'WORK_ORDER' | 'ROUTE' | 'PARTNER' | 'USER';
    title: string;
    subtitle: string;
    path: string;
  }>>([]);
  const [searching, setSearching] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);

  const refreshUnread = useCallback(async () => {
    if (!context?.modules.includes('notifications')) return;
    const response = await apiRequest<{ unread: number }>(
      '/api/v1/notifications',
      { accessToken: readToken(tenantTokenKey) }
    );
    setUnread(response.unread);
  }, [context?.modules]);

  useEffect(() => {
    async function load() {
      try {
        const response = await apiRequest<TenantContextData>(
          '/api/v1/tenant/context',
          { accessToken: token }
        );
        setContext(response);
        setLocale(response.user.locale);
        document.documentElement.style.setProperty(
          '--brand',
          response.company.primaryColor
        );
        document.documentElement.style.setProperty(
          '--accent',
          response.company.secondaryColor
        );
        document.title = `${response.company.displayName} · Šihterica`;
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) {
          clearToken(tenantTokenKey);
          navigate('/login', { replace: true });
          return;
        }
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'Aplikacija se trenutačno ne može učitati.'
        );
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [navigate, setLocale, token]);

  useEffect(() => {
    if (context) void refreshUnread().catch(() => undefined);
  }, [context, refreshUnread]);

  const modules = useMemo(() => new Set(context?.modules ?? []), [context]);

  const updateCurrentUser = useCallback((patch: Partial<TenantContextData['user']>) => {
    setContext((current) => current ? {
      ...current,
      user: { ...current.user, ...patch }
    } : current);
  }, []);

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInput.current?.focus();
      }
      if (event.key === 'Escape' && document.activeElement === searchInput.current) {
        setGlobalQuery('');
        setSearchResults([]);
        searchInput.current?.blur();
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, []);

  useEffect(() => {
    const query = globalQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const delay = window.setTimeout(() => {
      void apiRequest<{ items: typeof searchResults }>(
        `/api/v1/search?q=${encodeURIComponent(query)}`,
        { accessToken: readToken(tenantTokenKey) }
      ).then((response) => setSearchResults(response.items))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 220);
    return () => window.clearTimeout(delay);
  }, [globalQuery]);

  async function changeLocale(nextLocale: AppLocale) {
    setLocale(nextLocale);
    if (!context) return;
    setContext({
      ...context,
      user: { ...context.user, locale: nextLocale }
    });
    await apiRequest('/api/v1/tenant/preferences/locale', {
      method: 'PATCH',
      accessToken: readToken(tenantTokenKey),
      body: JSON.stringify({ locale: nextLocale })
    }).catch(() => undefined);
  }

  function logout() {
    void apiRequest('/api/v1/auth/logout', {
      method: 'POST',
      accessToken: token
    }).catch(() => undefined);
    clearToken(tenantTokenKey);
    navigate('/login', { replace: true });
  }

  if (loading) {
    return <div className="app-loading"><span /><p>{t('loading.app')}</p></div>;
  }
  if (!context) {
    return <main className="fatal-state"><div className="form-error">{error}</div></main>;
  }

  const closeMenu = () => setMobileMenu(false);
  const openMenu = () => setMobileMenu(true);
  const initials = `${context.user.firstName[0]}${context.user.lastName[0]}`;
  const primaryMobilePath =
    location.pathname === '/app' ||
    location.pathname.startsWith('/app/tasks') ||
    location.pathname.startsWith('/app/attendance') ||
    location.pathname.startsWith('/app/leave');

  return (
    <TenantAppContext.Provider
      value={{ context, modules, unread, refreshUnread, updateCurrentUser }}
    >
      <div className="app-shell">
        <aside
          id="tenant-navigation"
          className={`app-sidebar ${mobileMenu ? 'is-open' : ''}`}
          aria-hidden={!mobileMenu ? undefined : false}
        >
          <div className="app-sidebar__top">
            <Logo name={context.company.displayName} logoUrl={context.company.logoUrl} />
            <button className="icon-button mobile-only" onClick={closeMenu}>
              <X size={20} />
            </button>
          </div>
          <nav className="app-nav">
            <NavLink to="/app" end onClick={closeMenu}>
              <LayoutDashboard size={19} /> {t('nav.dashboard')}
            </NavLink>
            {modules.has('tasks') && (
              <NavLink to="/app/tasks" onClick={closeMenu}>
                <CheckCircle2 size={19} /> {t('nav.tasks')}
              </NavLink>
            )}
            {modules.has('partners') && (
              <NavLink to="/app/partners" onClick={closeMenu}>
                <Building2 size={19} /> {t('nav.partners')}
              </NavLink>
            )}
            {modules.has('work_orders') && (
              <NavLink to="/app/work-orders" onClick={closeMenu}>
                <BriefcaseBusiness size={19} /> {t('nav.workOrders')}
              </NavLink>
            )}
            {modules.has('routes') && context.user.permissions.includes('routes.read') && (
              <NavLink to="/app/routes" onClick={closeMenu}>
                <Route size={19} /> {t('nav.routes')}
              </NavLink>
            )}
            {modules.has('shifts') && (
              <NavLink to="/app/shifts" onClick={closeMenu}>
                <CalendarDays size={19} /> {t('nav.shifts')}
              </NavLink>
            )}
            {modules.has('attendance') && (
              <NavLink to="/app/attendance" onClick={closeMenu}>
                <QrCode size={19} /> {t('nav.attendance')}
              </NavLink>
            )}
            {modules.has('attendance') && context.user.permissions.includes('work_time.read') && (
              <NavLink to="/app/work-time" onClick={closeMenu}>
                <ClipboardCheck size={19} /> {t('nav.workTime')}
              </NavLink>
            )}
            {modules.has('attendance') && context.user.permissions.includes('field_work.read') && (
              <NavLink to="/app/field-work" onClick={closeMenu}>
                <MapPinned size={19} /> {t('nav.fieldWork')}
              </NavLink>
            )}
            {modules.has('leave') && (
              <NavLink to="/app/leave" onClick={closeMenu}>
                <Clock3 size={19} /> {t('nav.leave')}
              </NavLink>
            )}
            {modules.has('documents') && (
              <NavLink to="/app/documents" onClick={closeMenu}>
                <FileText size={19} /> {t('nav.documents')}
              </NavLink>
            )}
            {context.user.permissions.includes('users.read') && (
              <NavLink to="/app/employees" onClick={closeMenu}>
                <Users size={19} /> {t('nav.employees')}
              </NavLink>
            )}
          </nav>
          <div className="app-sidebar__bottom">
            <NavLink to="/app/settings" onClick={closeMenu}>
              <Settings size={19} /> {t('nav.settings')}
            </NavLink>
            <button onClick={logout}><LogOut size={19} /> {t('action.logout')}</button>
            <div className="user-chip">
              <span className="avatar">{initials}</span>
              <span>
                <strong>{context.user.firstName} {context.user.lastName}</strong>
                <small>{context.user.jobTitle ?? context.user.email}</small>
              </span>
            </div>
          </div>
        </aside>

        {mobileMenu && <button className="sidebar-scrim" onClick={closeMenu} />}

        <main className="app-main">
          <header className="app-topbar">
            <button
              type="button"
              className="icon-button mobile-only mobile-topbar-menu"
              onClick={openMenu}
              aria-label={t('nav.more')}
              aria-controls="tenant-navigation"
              aria-expanded={mobileMenu}
            >
              <Menu size={21} />
            </button>
            <div className={`global-search ${globalQuery.trim().length >= 2 ? 'is-open' : ''}`}>
              <Search size={18} />
              <input
                ref={searchInput}
                value={globalQuery}
                onChange={(event) => setGlobalQuery(event.target.value)}
                placeholder={t('search.placeholder')}
                autoComplete="off"
              />
              <kbd>Ctrl K</kbd>
              {globalQuery.trim().length >= 2 && (
                <span className="global-search__results">
                  {searching && <em>Pretražujem…</em>}
                  {!searching && searchResults.map((result) => (
                    <button
                      type="button"
                      key={`${result.type}-${result.id}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        navigate(result.path);
                        setGlobalQuery('');
                        setSearchResults([]);
                      }}
                    >
                      <small>{result.type.replace('_', ' ')}</small>
                      <strong>{result.title}</strong>
                      <span>{result.subtitle}</span>
                    </button>
                  ))}
                  {!searching && !searchResults.length && <em>Nema rezultata.</em>}
                </span>
              )}
            </div>
            <LanguageSwitcher
              value={locale}
              onChange={changeLocale}
              ariaLabel={t('settings.language')}
            />
            {modules.has('notifications') && (
              <button
                className="notification-button"
                onClick={() => navigate('/app/notifications')}
                aria-label={t('nav.notifications')}
              >
                <Bell size={20} />
                {unread > 0 && <span>{unread > 99 ? '99+' : unread}</span>}
              </button>
            )}
            <button
              className="topbar-avatar"
              onClick={() => navigate('/app/settings')}
              aria-label="Otvori moj profil"
            >
              {initials}
            </button>
          </header>

          {error && <div className="shell-error form-error">{error}</div>}
          {children}
        </main>

        <nav className="mobile-bottom-nav">
          <NavLink to="/app" end><LayoutDashboard /><span>{t('nav.dashboard')}</span></NavLink>
          {modules.has('tasks') && (
            <NavLink to="/app/tasks"><CheckCircle2 /><span>{t('nav.tasks')}</span></NavLink>
          )}
          {modules.has('attendance') && (
            <NavLink to="/app/attendance"><QrCode /><span>{t('nav.attendance')}</span></NavLink>
          )}
          {modules.has('leave') && (
            <NavLink to="/app/leave"><CalendarDays /><span>{t('nav.leave')}</span></NavLink>
          )}
          <button
            type="button"
            className={mobileMenu || !primaryMobilePath ? 'is-active' : ''}
            onClick={openMenu}
            aria-label={t('nav.more')}
            aria-controls="tenant-navigation"
            aria-expanded={mobileMenu}
          >
            <Grid2X2 /><span>{t('nav.more')}</span>
          </button>
        </nav>
      </div>
    </TenantAppContext.Provider>
  );
}
